import React from "react";
import Superior from "../Assets/Images/Superior.svg";
import Optima from "../Assets/Images/Optima.svg";
import Vital from "../Assets/Images/Vital.svg";
import LogoRed from "../Assets/Images/LogoRed.svg";
import linkedin from "../Assets/Images/linkedin.svg";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../Hooks/useTranslation";

const Footer = () => {
  const translations = useTranslation();
  const location = useLocation();

  const pagesWithFAQs = [
    "/",
    "/universal-life-insurance",
    "/term-life-insurance",
    "/indexed-universal-life-insurance",
    "/hospitals",
    "/compare",
  ];

  const currentPageHasFAQs = pagesWithFAQs.includes(location.pathname);
  const faqLink = currentPageHasFAQs
    ? location.pathname === "/"
      ? "/?section=faqs"
      : `${location.pathname}?section=faqs`
    : "/?section=faqs";

  return (
    <div className="mainfooterdiv bg-[#F7F6F2]" id="footer">
      <div className="container">
        <div className="mx-auto pt-0">
          <div className="px-6 xl:px-12 lg:py-24 py-12 Myriad-font">
            {/* MOBILE TOP (<md): Logo+LinkedIn row, then compact contact block */}
            <div className="md:hidden reveal-up pb-6 border-b border-[#C4BD7D] mb-6">
              {/* Logo + LinkedIn in same row */}
              <div className="flex justify-between items-start mb-4">
                <img
                  src={LogoRed}
                  alt="American Fidelity International Ltd."
                  className="h-[22px] w-auto block"
                />
                <Link
                  to="https://www.linkedin.com/company/american-fidelity-international-bermuda-ltd"
                  target="_blank"
                  aria-label="LinkedIn"
                  className="bg-[#EFEDE7] border border-[#E5E3DD] rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0"
                >
                  <img src={linkedin} alt="LinkedIn" className="w-4 h-4" />
                </Link>
              </div>
              {/* Compact contact block */}
              <div className="flex flex-col gap-2 text-[12px] leading-[19px] tracking-[-0.24px] text-[#565454]">
                <p className="m-0">{translations.footer.address}</p>
                <p className="m-0">Telephone 1 (246) 367-8874</p>
                <p className="m-0">Email: {translations.footer.email}</p>
                <p className="m-0">Fax: (441) 292-0665</p>
              </div>
            </div>

            {/* TABLET+ TOP (md+): 2 columns with detailed contact */}
            <div className="hidden md:flex flex-row md:gap-[58px] md:pb-10 reveal-up">
              {/* Logo + Address (LEFT COLUMN) */}
              <div className="flex flex-col items-start gap-6 md:gap-10 md:w-[281px] md:flex-shrink-0">
                <img
                  src={LogoRed}
                  alt="American Fidelity International Ltd."
                  className="h-[27px] w-auto block"
                />
                <p className="text-[14px] leading-[22px] text-[#565454] m-0">
                  {translations.footer.address}
                </p>
              </div>

              {/* LinkedIn + Contact info (RIGHT COLUMN) */}
              <div className="flex flex-col items-start gap-6 md:gap-10 md:w-[246px] md:flex-shrink-0">
                {/* LinkedIn icon */}
                <Link
                  to="https://www.linkedin.com/company/american-fidelity-international-bermuda-ltd"
                  target="_blank"
                  aria-label="LinkedIn"
                  className="bg-[#EFEDE7] border border-[#E5E3DD] rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0"
                >
                  <img src={linkedin} alt="LinkedIn" className="w-4 h-4" />
                </Link>

                {/* Contact details */}
                <div className="flex flex-col gap-4 text-[14px] leading-[22px] text-[#565454]">
                  <div>
                    <p className="m-0">Telephone:</p>
                    <p className="m-0">1 (246) 367-8874</p>
                  </div>
                  <div>
                    <p className="m-0">Email:</p>
                    <p className="m-0">{translations.footer.email}</p>
                  </div>
                  <div>
                    <p className="m-0">Fax:</p>
                    <p className="m-0">(441) 292-0665</p>
                  </div>
                </div>
              </div>
            </div>

            {/* DIVIDER (tablet+ only) */}
            <div className="hidden md:block h-px bg-[#AFA07B] my-4"></div>

            {/* MENU ROW: 5 columns */}
            <div className="flex flex-wrap lg:flex-nowrap justify-between lg:gap-8 lg:pt-6 footer-menu-column">
              {/* Home */}
              <div className="w-1/2 lg:w-auto lg:flex-1 reveal-up reveal-delay-1">
                <Link to="/">
                  <h4 className="text-[14px] font-semibold mb-4 lg:mb-10 tracking-[-0.28px]">
                    {translations.footer.navLinks.Home}
                  </h4>
                </Link>
                <ul className="text-[12px] md:text-[10.5px] xl:text-[14px] mb-6 lg:mb-0">
                  <Link to="/?section=About-Us">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.About_Us}
                    </li>
                  </Link>
                  <Link to="/?section=our-products">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Products}
                    </li>
                  </Link>
                  <Link to={faqLink}>
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.FAQs}
                    </li>
                  </Link>
                </ul>
              </div>

              {/* About Us */}
              <div className="w-1/2 lg:w-auto lg:flex-1 reveal-up reveal-delay-2">
                <Link to="/about">
                  <h4 className="text-[14px] font-semibold mb-4 lg:mb-10 tracking-[-0.28px]">
                    {translations.footer.navLinks.About_Us}
                  </h4>
                </Link>
                <ul className="text-[12px] md:text-[10.5px] xl:text-[14px] mb-6 lg:mb-0">
                  <Link to="/about?section=american-fidelity">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.American_Fidelity}
                    </li>
                  </Link>
                  <Link to="/about?section=values-guide-us">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Learn_More}
                    </li>
                  </Link>
                  <Link to="/about?section=about-international-client-id">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.AFIBL}
                    </li>
                  </Link>
                </ul>
              </div>

              {/* Universal Life */}
              <div className="w-1/2 lg:w-auto lg:flex-[1.2] reveal-up reveal-delay-3">
                <div className="flex flex-col items-start md:flex-row md:items-center gap-2 md:gap-1.5 mb-2 md:mb-4 lg:mb-10">
                  <div className="bg-[#F7F6F2] rounded-[9.5px] flex items-center justify-center flex-shrink-0 p-[6px]">
                    <img
                      src={Superior}
                      alt=""
                      width={20}
                      height={17}
                    />
                  </div>
                  <Link to="/universal-life-insurance">
                    <h4 className="text-[14px] font-semibold tracking-[-0.28px] whitespace-nowrap">
                      {translations.footer.navLinks.Universal_Life}
                    </h4>
                  </Link>
                </div>
                <ul className="text-[12px] md:text-[10.5px] xl:text-[14px] mb-6 lg:mb-0">
                  <Link to="/universal-life-insurance?section=Benefits">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Benefits}
                    </li>
                  </Link>
                  <Link to="/universal-life-insurance?section=how-it-works">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.How_It_Works}
                    </li>
                  </Link>
                  <Link to="/universal-life-insurance?section=Universal-types">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Universal_Life}
                    </li>
                  </Link>
                  <Link to="/indexed-universal-life-insurance">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Indexed_Universal_Life}
                    </li>
                  </Link>
                </ul>
              </div>

              {/* Select Term */}
              <div className="w-1/2 lg:w-auto lg:flex-1 reveal-up reveal-delay-4">
                <div className="flex flex-col items-start md:flex-row md:items-center gap-2 md:gap-1.5 mb-2 md:mb-4 lg:mb-10">
                  <div className="bg-[#F7F6F2] rounded-[9.5px] flex items-center justify-center flex-shrink-0 p-[6px]">
                    <img
                      src={Optima}
                      alt=""
                      width={20}
                      height={17}
                    />
                  </div>
                  <Link to="/term-life-insurance">
                    <h4 className="text-[14px] font-semibold tracking-[-0.28px] whitespace-nowrap">
                      {translations.footer.navLinks.Select_Term}
                    </h4>
                  </Link>
                </div>
                <ul className="text-[12px] md:text-[10.5px] xl:text-[14px] mb-6 lg:mb-0">
                  <Link to="/term-life-insurance?section=terms-benefits">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Benefits}
                    </li>
                  </Link>
                  <Link to="/term-life-insurance?section=improve-benefits">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.AdditionalBenefits}
                    </li>
                  </Link>
                  <Link to="/term-life-insurance?section=types-of-coverage">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.CoverageType}
                    </li>
                  </Link>
                </ul>
              </div>

              {/* Critical Illness */}
              <div className="w-1/2 lg:w-auto lg:flex-1 reveal-up reveal-delay-5">
                <div className="flex flex-col items-start md:flex-row md:items-center gap-2 md:gap-1.5 mb-2 md:mb-4 lg:mb-10">
                  <div className="bg-[#F7F6F2] rounded-[9.5px] flex items-center justify-center flex-shrink-0 p-[6px]">
                    <img
                      src={Vital}
                      alt=""
                      width={20}
                      height={17}
                    />
                  </div>
                  <Link to="/critical-illness-insurance">
                    <h4 className="text-[14px] font-semibold tracking-[-0.28px] whitespace-nowrap">
                      {translations.footer.navLinks.Critical_Illness}
                    </h4>
                  </Link>
                </div>
                <ul className="text-[12px] md:text-[10.5px] xl:text-[14px] mb-6 lg:mb-0">
                  <Link to="/critical-illness-insurance?section=WhyChooseIllness">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Benefits}
                    </li>
                  </Link>
                  <Link to="/critical-illness-insurance?section=critical-illness-covered">
                    <li className="lg:mb-4 mb-2 leading-[19px] md:leading-[22px] tracking-[-0.24px] md:tracking-normal text-[#565454]">
                      {translations.footer.navLinks.Critical_Illness}
                    </li>
                  </Link>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
